import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import { AdminClamping, UpsertAdminClampingBody } from "../../../../../http/patrol/types/patrolClampings";
import { AdminPatrolSystem } from "../../../../../http/patrol/types/patrolSystems";
import { AdminRoofSubstructure } from "../../../../../http/patrol/types/roofSubstructures";
import { useClampingsApi } from "../../../../../http/patrol/usePatrolClampings";
import { usePatrolSystemsApi } from "../../../../../http/patrol/usePatrolSystems";
import { useRoofSubstructuresApi } from "../../../../../http/patrol/useRoofSubstructures";
import { AdminItem, SearchAdminItem } from "../../../../../http/types/items";
import { useItemsApi } from "../../../../../http/useItems";
import ControlledNumericWithQuantity from "../../../../ui/ControlledNumericWithQuantity";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import Switch from "../../../../ui/Switch";
import { CustomAsyncSelect, CustomSelect } from "../../../../ui/select/CustomSelect";

interface ClampingFormBody {
  system: AdminPatrolSystem | null;
  roofSubstructure: AdminRoofSubstructure | null;
  includeSelectedComponent: boolean;
  topItem: AdminItem | null;
  topExtraItem: AdminItem | null;
  bottomItems: AdminItem[];
}

interface ClampingDetailsProps {
  clamping?: AdminClamping;
  onSave: () => void;
}

const ClampingForm: FC<ClampingDetailsProps> = ({ clamping, onSave }: ClampingDetailsProps) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const { patrolSystemId } = useParams<{ patrolSystemId: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const aborter = useRef(new AbortController());
  const [defaultOptions, setDefaultOptions] = useState<SearchAdminItem[]>([]);

  const roofSubstructureApi = useRoofSubstructuresApi();
  const anchorMappingsApi = useClampingsApi();
  const itemsApi = useItemsApi();
  const systemsApi = usePatrolSystemsApi();

  const roofSubstructureResult = roofSubstructureApi.useAdminRoofSubstructures({}, aborter.current.signal);
  const systemResult = systemsApi.useAdminPatrolSystems({}, aborter.current.signal);

  const defaultValues: ClampingFormBody = {
    system: clamping?.system ?? null,
    roofSubstructure: clamping?.roofSubstructure ?? null,
    topItem: clamping?.topItem ?? null,
    topExtraItem: clamping?.topExtraItem ?? null,
    includeSelectedComponent: !!clamping?.includeSelectedComponent,
    bottomItems: clamping?.bottomItems ?? [],
  };

  const onSubmit = () => async (formData: ClampingFormBody) => {
    setIsSubmitting(true);

    const body: UpsertAdminClampingBody = {
      systemId: patrolSystemId ?? formData.system?.id!,
      roofSubstructureId: formData.roofSubstructure?.id,
      topItemId: formData.topItem?.id!,
      topExtraItemId: formData.topExtraItem?.id,
      includeSelectedComponent: !!formData.includeSelectedComponent,
      bottomItemsIds: formData.bottomItems.map((b) => b.id),
    };

    if (clamping?.id) {
      try {
        await anchorMappingsApi.updateAdminClamping(clamping.id, body);
        onSave();
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    } else {
      try {
        await anchorMappingsApi.insertAdminClamping(body);
        onSave();
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Form<ClampingFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting}
      onSubmit={onSubmit}
      fullWidth
    >
      {({ control, setValue, reset, watch }) => {
        useEffect(() => {
          clamping && reset(defaultValues);
        }, [clamping]);

        const includeSelectedComponent = watch("includeSelectedComponent");

        const searchTerm = useRef("");
        const aborter = useRef(new AbortController());

        const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

        return (
          <FormFieldsContainer fullWidth>
            <Controller
              name="system"
              control={control}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.systems.singular}
                  error={fieldState.error}
                  hidden={!!patrolSystemId}
                >
                  {({ labelId }) => (
                    <CustomSelect<AdminPatrolSystem>
                      {...field}
                      inputId={labelId}
                      options={systemResult.data?.items ?? []}
                      isDisabled={!!systemResult.error || isSubmitting}
                      isLoading={systemResult.isLoading}
                      isClearable
                      getOptionValue={(x) => x.id}
                      getOptionLabel={(x) => x.name}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="roofSubstructure"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.roofSubstructures.singular} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <CustomSelect<AdminRoofSubstructure>
                      {...field}
                      inputId={labelId}
                      options={roofSubstructureResult.data?.items ?? []}
                      isDisabled={!!roofSubstructureResult.error || isSubmitting}
                      isLoading={roofSubstructureResult.isLoading}
                      isClearable
                      getOptionValue={(x) => x.id}
                      getOptionLabel={(x) => x.name}
                    />
                  )}
                </FormField>
              )}
            />
            <FormField label={i18n.translation.common.includeSelectedComponent} isOptional>
              {({ labelId }) => (
                <Switch
                  id={labelId}
                  checked={includeSelectedComponent}
                  onChange={(checked) => setValue("includeSelectedComponent", checked)}
                />
              )}
            </FormField>
            <Controller
              name="topItem"
              control={control}
              rules={{ required: !includeSelectedComponent }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.common.topItem}
                  error={fieldState.error}
                  isOptional={includeSelectedComponent}
                >
                  {({ labelId }) => {
                    const searchTerm = useRef("");
                    const aborter = useRef(new AbortController());

                    const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

                    // Fetch initial data on mount
                    useEffect(() => {
                      loadOptions(searchTerm.current).then(setDefaultOptions);
                    }, []);

                    return (
                      <CustomAsyncSelect
                        {...field}
                        inputId={labelId}
                        defaultOptions={defaultOptions}
                        loadOptions={loadOptions}
                        isClearable
                        getOptionLabel={(x) => x.code}
                        getOptionValue={(x) => x.id}
                        onChange={(value) => setValue("topItem", value ?? null)}
                      />
                    );
                  }}
                </FormField>
              )}
            />
            <Controller
              name="topExtraItem"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.topExtraItem} error={fieldState.error} isOptional>
                  {({ labelId }) => {
                    const searchTerm = useRef("");
                    const aborter = useRef(new AbortController());

                    const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

                    return (
                      <CustomAsyncSelect
                        {...field}
                        inputId={labelId}
                        defaultOptions={defaultOptions}
                        loadOptions={loadOptions}
                        isClearable
                        getOptionLabel={(x) => x.code}
                        getOptionValue={(x) => x.id}
                        onChange={(value) => setValue("topExtraItem", value ?? null)}
                      />
                    );
                  }}
                </FormField>
              )}
            />
            <Controller
              name="bottomItems"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.bottomItems} error={fieldState.error}>
                  {field.value.map((x) => (
                    <ControlledNumericWithQuantity
                      item={{
                        id: x.id,
                        display: x.code,
                        disabled: true,
                      }}
                      onDelete={(id) =>
                        setValue(
                          "bottomItems",
                          field.value.filter((bi) => bi.id !== id),
                        )
                      }
                    />
                  ))}
                  <CustomAsyncSelect<AdminItem>
                    defaultOptions={defaultOptions}
                    loadOptions={loadOptions}
                    isClearable
                    getOptionLabel={(x) => x.code}
                    getOptionValue={(x) => x.id}
                    onChange={(value) => value && setValue("bottomItems", [...field.value, value])}
                    value={null}
                  />
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default ClampingForm;
